import React, { memo, useState, Fragment } from 'react';
import { camelize } from 'humps';
import classnames from 'classnames';

import { Spinner } from '../../helpers/Spinner';

import style from './style.module.css';

const StatisticReportTableComponent = ({ statistics, isLoading, user, onShareLink }) => {
  const [expanded, setExpanded] = useState({});
  const [loadingCell, setLoadingCell] = useState(null);

  const toggle = (id, currentValue) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !currentValue,
    }));
  };

  const columns = statistics?.get('columns');
  const listsColumn = columns?.find(column => column.get('key') === 'lists');
  const approachesColumns = columns?.filter(column => column.get('key').startsWith('approaches'));
  const restColumns = columns?.filter(
    column => column.get('key') !== 'lists' && !column.get('key').startsWith('approaches'),
  );
  const callsColumn = restColumns?.first();
  const leadProgressionColumns = restColumns?.skip(1);
  const sortedColumns = listsColumn ? [listsColumn, ...(approachesColumns ?? []), ...(restColumns ?? [])] : columns;

  const renderRow = (module, level = 0, parentDefaultExpanded = false, parentRowId = '') => {
    const type = module.get('type');

    const naturalKey =
      type === 'module'
        ? module.getIn(['owner', 'id'])
        : type === 'person'
        ? module.get('membershipId') ?? module.get('userId')
        : module.get('dealmakerId') ?? module.get('directorUserId');
    const rowId = parentRowId ? `${parentRowId}>${type}-${naturalKey}` : `${type}-${naturalKey}`;

    const title = module.get('userTitle');
    const children = module.get('children');
    const hasChildren = children?.size > 0;
    const isUserModule = module.getIn(['owner', 'id']) === user?.get('moduleId');
    const isExpanded = rowId in expanded ? expanded[rowId] : isUserModule || parentDefaultExpanded;
    const isInactive = type !== 'module' && Boolean(module.get('inactive'));

    const rowClassName =
      {
        module: 'text-uppercase text-bold name-column row-module',
        primary_dealmaker: 'text-bold name-column',
        secondary_dealmaker: 'text-bold name-column',
        direct_reports: 'text-bold name-column',
        person: '',
      }[type] || '';

    const paddingLevel = type === 'person' ? Math.max(level - 1, 0) : level;

    return (
      <Fragment key={rowId}>
        <tr className={rowClassName || undefined} style={isInactive ? { opacity: 0.3 } : undefined}>
          <td style={{ paddingLeft: `${20 + paddingLevel * 20}px` }}>
            {hasChildren ? (
              <span className="toggle" onClick={() => toggle(rowId, isExpanded)}>
                {isExpanded ? <span className="fa fa-chevron-down" /> : <span className="fa fa-chevron-right" />}
              </span>
            ) : (
              <span className="toggle" />
            )}

            {type === 'direct_reports' ? 'Individual' : module.get('name')}

            {type === 'person' && title && <small className="text-muted text-uppercase ml8">{title}</small>}
            {isInactive && <small className="text-muted text-uppercase ml8">Inactive</small>}
          </td>

          {sortedColumns.map(column => {
            const metricKey = camelize(column.get('key'));
            const attainmentPercent = module.getIn(['metrics', metricKey, 'attainmentPercent']);
            const benchmark = module.getIn(['metrics', metricKey, 'benchmark']);
            const actual = module.getIn(['metrics', metricKey, 'actual']);
            const isShareLink = actual > 0;
            const isColorable = benchmark > 0 || actual > 0;
            const effectiveAttainmentPercent = benchmark > 0 ? attainmentPercent : actual * 100;
            const cellClassName = classnames('text-right', 'position-relative', {
              'text-muted': !isColorable,
              'cell-danger': isColorable && effectiveAttainmentPercent >= 0 && effectiveAttainmentPercent <= 74,
              'cell-warning': isColorable && effectiveAttainmentPercent >= 75 && effectiveAttainmentPercent <= 99,
              'cell-success': isColorable && effectiveAttainmentPercent >= 100 && effectiveAttainmentPercent <= 149,
              'cell-info': isColorable && effectiveAttainmentPercent >= 150,
              'cursor-pointer': isShareLink,
            });
            const cellKey = `${column.get('key')}-${rowId}`;
            const isCellLoading = loadingCell === cellKey;

            const handleShareLinkDoubleClick = async () => {
              if (!isShareLink) {
                return undefined;
              }

              setLoadingCell(cellKey);
              await onShareLink({
                metric: column.get('key'),
                userId: module.get('userId'),
                userType: type,
                moduleId: module.get('moduleId') ?? module.get('directorUserId') ?? null,
                dealmakerType: module.get('dealmakerType') ?? null,
                dealmakerId: module.get('dealmakerId') ?? null,
              });
              setLoadingCell(null);
            };

            return (
              <td key={column.get('key')} className={cellClassName} onDoubleClick={handleShareLinkDoubleClick}>
                {isCellLoading && (
                  <div className="position-absolute" style={{ left: '35px', top: '25px' }}>
                    <Spinner size="sm" />
                  </div>
                )}
                <>
                  <div>{actual ?? '—'}</div>
                  <div>/ {benchmark ?? '—'}</div>
                </>
              </td>
            );
          })}
        </tr>

        {hasChildren &&
          isExpanded &&
          children.map(child => renderRow(child, level + 1, isUserModule || parentDefaultExpanded, rowId))}
      </Fragment>
    );
  };

  return (
    <div className="position-relative">
      {isLoading && <Spinner />}
      <table className="table table-blue table-hover mb0 statistic-report-table">
        <thead className={style.stickyHeader}>
          <tr>
            <th rowSpan="3">Module / Dealmaker / Individual</th>
            <th colSpan="5">
              Outreach
              <span className="ml5 label label-primary font-size-10">Top of funnel</span>
            </th>
            <th colSpan="4" rowSpan="2">
              Lead Progression
              <span className="ml5 label label-info font-size-10">Conversion</span>
            </th>
          </tr>
          <tr className="col-headers">
            <th className="outreach-spacer" />
            <th className="approaches" colSpan="3">
              Approaches
            </th>
            <th className="outreach-spacer" />
          </tr>
          <tr className="col-headers">
            {listsColumn && (
              <th key={listsColumn.get('key')} title={listsColumn.get('title')}>
                {listsColumn.get('label')}
              </th>
            )}
            {approachesColumns?.map(column => (
              <th key={column.get('key')} title={column.get('title')}>
                {column.get('label')}
              </th>
            ))}
            {callsColumn && (
              <th key={callsColumn.get('key')} title={callsColumn.get('title')}>
                {callsColumn.get('label')}
              </th>
            )}
            {leadProgressionColumns?.map(column => (
              <th key={column.get('key')} title={column.get('title')}>
                {column.get('label')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{statistics?.get('modules')?.map(module => renderRow(module))}</tbody>
      </table>
    </div>
  );
};

export const StatisticReportTable = memo(StatisticReportTableComponent);
